import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { useSession } from '@/lib/session';

/**
 * Where a buyer is having things delivered.
 *
 * ── THE BUG THIS REPLACES ───────────────────────────────────────────────────
 *
 * The header said "Deliver to Wuse II, Abuja" to every buyer in the country,
 * hardcoded, in two places. That is bad on its own. What made it worse is that
 * the app then passed NOTHING as the `state` parameter the home and search
 * endpoints already accept — so the label asserted a location and the queries
 * behind it ignored location entirely.
 *
 * Delivery pricing resolves by state and LGA, and "verified pharmacies near
 * you" means pharmacies that can actually reach you. Both were being asked for
 * the whole country while the screen claimed Abuja.
 *
 * ── WHERE THE ANSWER COMES FROM, IN ORDER ───────────────────────────────────
 *
 *   1. the buyer's DEFAULT ADDRESS, when they are signed in and have one. It
 *      is the address a delivery will actually go to, which makes it the only
 *      authoritative answer.
 *   2. whatever they last chose here, kept in this browser.
 *   3. nothing — and the header asks rather than inventing a place.
 *
 * Device location is offered, never taken. It is a permission prompt the buyer
 * answers, and a refusal is a perfectly good outcome that leaves (2) and (3).
 */

export interface DeliveryLocation {
  state: string;
  lga: string | null;
  /** Where this came from, so the UI can say "your default address". */
  source: 'address' | 'chosen' | 'device';
}

const STORAGE_KEY = 'chicare.delivery-location';

/**
 * Nigerian state centroids, for turning a GPS fix into a state.
 *
 * ── WHY THIS IS A TABLE AND NOT A GEOCODING CALL ────────────────────────────
 *
 * Reverse geocoding means an API key, a per-request cost, a dependency that
 * can be down, and a buyer's exact coordinates leaving ChiCare for a third
 * party on the home screen. For a question as coarse as "which state are you
 * in" that is a great deal to pay.
 *
 * Nearest centroid gets the state right for anyone not standing on a border,
 * which is what delivery pricing resolves by. It costs nothing, works offline
 * once the page has loaded, and sends the buyer's position nowhere.
 *
 * It does NOT give an LGA. That is why a device fix fills in the state and
 * leaves the LGA null rather than guessing at one.
 */
const STATE_CENTROIDS: ReadonlyArray<readonly [string, number, number]> = [
  ['Abia', 5.45, 7.52],
  ['Adamawa', 9.33, 12.4],
  ['Akwa Ibom', 5.01, 7.86],
  ['Anambra', 6.21, 6.94],
  ['Bauchi', 10.31, 9.84],
  ['Bayelsa', 4.77, 6.07],
  ['Benue', 7.33, 8.74],
  ['Borno', 11.85, 13.16],
  ['Cross River', 5.87, 8.6],
  ['Delta', 5.71, 5.94],
  ['Ebonyi', 6.26, 8.08],
  ['Edo', 6.63, 5.93],
  ['Ekiti', 7.72, 5.31],
  ['Enugu', 6.53, 7.51],
  ['FCT', 9.06, 7.49],
  ['Gombe', 10.29, 11.17],
  ['Imo', 5.57, 7.06],
  ['Jigawa', 12.23, 9.56],
  ['Kaduna', 10.37, 7.71],
  ['Kano', 11.75, 8.52],
  ['Katsina', 12.99, 7.6],
  ['Kebbi', 11.49, 4.24],
  ['Kogi', 7.73, 6.69],
  ['Kwara', 8.97, 4.39],
  ['Lagos', 6.52, 3.38],
  ['Nasarawa', 8.54, 8.32],
  ['Niger', 9.93, 5.6],
  ['Ogun', 7.0, 3.35],
  ['Ondo', 7.25, 5.2],
  ['Osun', 7.56, 4.52],
  ['Oyo', 8.0, 3.94],
  ['Plateau', 9.22, 9.52],
  ['Rivers', 4.82, 6.99],
  ['Sokoto', 13.06, 5.24],
  ['Taraba', 7.87, 11.0],
  ['Yobe', 12.0, 11.5],
  ['Zamfara', 12.17, 6.66],
] as const;

/** Straight-line kilometres. Good enough to pick a state from 37. */
function distanceKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const earthKm = 6371;

  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);

  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;

  return earthKm * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

export function stateNearest(lat: number, lng: number): string {
  let best = STATE_CENTROIDS[0]!;
  let bestKm = Number.POSITIVE_INFINITY;

  for (const entry of STATE_CENTROIDS) {
    const km = distanceKm(lat, lng, entry[1], entry[2]);

    if (km < bestKm) {
      bestKm = km;
      best = entry;
    }
  }

  return best[0];
}

function readStored(): DeliveryLocation | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<DeliveryLocation>;

    return parsed.state ? { state: parsed.state, lga: parsed.lga ?? null, source: 'chosen' } : null;
  } catch {
    // Private windows, cleared site data, a browser that refuses storage. None
    // of these are errors here — the header simply asks instead.
    return null;
  }
}

export function useDeliveryLocation() {
  const { user } = useSession();
  const [chosen, setChosen] = useState<DeliveryLocation | null>(() =>
    typeof window === 'undefined' ? null : readStored(),
  );

  // Only for a signed-in buyer: an anonymous visitor has no addresses, and
  // asking for them would be a guaranteed 401 on every home screen.
  const { data: addresses } = useQuery({
    queryKey: ['addresses'],
    queryFn: ({ signal }) => api.addresses(signal),
    enabled: Boolean(user),
    staleTime: 5 * 60 * 1000,
  });

  const defaultAddress = addresses?.find((one) => one.is_default) ?? addresses?.[0] ?? null;

  // The address wins. It is where a delivery will actually go, so a buyer who
  // has one should never be shown a different place in the header.
  const location: DeliveryLocation | null = defaultAddress
    ? { state: defaultAddress.state, lga: defaultAddress.lga, source: 'address' }
    : chosen;

  const choose = useCallback((next: DeliveryLocation | null) => {
    setChosen(next);

    try {
      if (next) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // Kept for this session only. Better than refusing the choice.
    }
  }, []);

  return { location, choose, hasAddress: Boolean(defaultAddress) };
}

/**
 * Ask the device where it is, once.
 *
 * Returns a state name or null. Never throws: a refused permission, a device
 * with no GPS and a timeout are all ordinary answers, and the caller's job is
 * to carry on asking the buyer rather than to handle an exception.
 */
export function useDeviceState() {
  const [asking, setAsking] = useState(false);
  const [denied, setDenied] = useState(false);

  const ask = useCallback(async (): Promise<string | null> => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      return null;
    }

    setAsking(true);

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          // Coarse is all this needs: the answer is one of 37 states. Asking
          // for high accuracy costs battery and a GPS lock for nothing.
          enableHighAccuracy: false,
          timeout: 10_000,
          maximumAge: 10 * 60 * 1000,
        });
      });

      return stateNearest(position.coords.latitude, position.coords.longitude);
    } catch {
      setDenied(true);

      return null;
    } finally {
      setAsking(false);
    }
  }, []);

  return { ask, asking, denied };
}

/** The canonical state list, for the picker. Served, never hardcoded. */
export function useStates(): string[] {
  const { data } = useQuery({
    queryKey: ['system', 'locations'],
    queryFn: ({ signal }) => api.locations(signal),
    staleTime: 24 * 60 * 60 * 1000,
  });

  const states = (data as { states?: string[] } | undefined)?.states;

  // The centroid table is the fallback rather than a second hardcoded list:
  // if it ever disagrees with the server, the picker and the geolocation
  // would offer different spellings of the same state.
  return states?.length ? states : STATE_CENTROIDS.map(([name]) => name);
}

/** "Wuse II, Abuja" or just "Lagos" — whatever is actually known. */
export function describeLocation(location: DeliveryLocation | null): string | null {
  if (!location) {
    return null;
  }

  return [location.lga, location.state].filter(Boolean).join(', ');
}

/** Kept in sync with the server on mount, so a stale state name self-corrects. */
export function useLocationSync(location: DeliveryLocation | null, states: string[]): void {
  useEffect(() => {
    if (!location || states.length === 0) {
      return;
    }

    // A state the server no longer recognises would be sent on every query and
    // silently match nothing. Better to notice here.
    if (!states.includes(location.state)) {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {
        // Nothing to do; the next render falls back to asking.
      }
    }
  }, [location, states]);
}
